// CheckboxField — canonical `checkbox` field primitive (comp-ε GAP-3b-i,
// CHG-2433). Single boolean toggle. Storage: boolean. The label renders
// inline to the right of the box (not above) since a checkbox reads as a
// statement; helperText / error still stack below via a wrapper.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('checkbox') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): label associated to the box by wrapping <label htmlFor=id> (the
// label is the clickable hit-target); validation error in a role="alert" live
// region; aria-invalid + aria-required on the box; aria-describedby links the
// box to its error + helper text nodes; native <input type=checkbox> focus/toggle.

import type { FieldPrimitiveProps } from "./types";
import { useFieldIds, describedBy } from "./FieldShell";

export type CheckboxFieldProps = FieldPrimitiveProps<boolean>;

export function CheckboxField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
}: CheckboxFieldProps) {
  const ids = useFieldIds();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label
        htmlFor={ids.inputId}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: "var(--color-text)",
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <input
          id={ids.inputId}
          name={name}
          type="checkbox"
          checked={value ?? false}
          disabled={disabled}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          aria-describedby={describedBy(ids, error, helperText)}
          onChange={(e) => onChange(e.target.checked)}
          style={{ width: 16, height: 16, accentColor: "var(--color-primary)" }}
        />
        {label ? (
          <span>
            {label}{" "}
            {required ? (
              <span aria-hidden="true" style={{ color: "var(--color-error)" }}>
                *
              </span>
            ) : null}
          </span>
        ) : null}
      </label>
      {helperText ? (
        <small
          id={ids.helperId}
          style={{ color: "var(--color-text-muted)", paddingLeft: 24 }}
        >
          {helperText}
        </small>
      ) : null}
      {error ? (
        <small
          id={ids.errorId}
          role="alert"
          style={{ color: "var(--color-error)", paddingLeft: 24 }}
        >
          {error}
        </small>
      ) : null}
    </div>
  );
}
